/**
 * Element Constants for PF MolView
 *
 * Atomic data for symmetry detection and molecular analysis.
 * Adapted from VMap constants.ts
 */

/**
 * Atomic radii (covalent/vdW hybrid) for distance-based bonding
 */
export const ATOMIC_RADII: Record<string, number> = {
  Ac: 1.88, Ag: 1.59, Al: 1.35, Am: 1.51, As: 1.21, Au: 1.50, B: 0.83,
  Ba: 1.34, Be: 0.35, Bi: 1.54, Br: 1.21, C: 0.68, Ca: 0.99, Cd: 1.69,
  Ce: 1.83, Cl: 0.99, Co: 1.33, Cr: 1.35, Cs: 1.67, Cu: 1.52, D: 0.23,
  Dy: 1.75, Er: 1.73, Eu: 1.99, F: 0.64, Fe: 1.34, Ga: 1.22, Gd: 1.79,
  Ge: 1.17, H: 0.23, Hf: 1.57, Hg: 1.70, Ho: 1.74, I: 1.40, In: 1.63,
  Ir: 1.32, K: 1.33, La: 1.87, Li: 0.68, Lu: 1.72, Mg: 1.10, Mn: 1.35,
  Mo: 1.47, N: 0.68, Na: 0.97, Nb: 1.48, Nd: 1.81, Ni: 1.50, Np: 1.55,
  O: 0.68, Os: 1.37, P: 1.05, Pa: 1.61, Pb: 1.54, Pd: 1.50, Pm: 1.80,
  Po: 1.68, Pr: 1.82, Pt: 1.50, Pu: 1.53, Ra: 1.90, Rb: 1.47, Re: 1.35,
  Rh: 1.45, Ru: 1.40, S: 1.02, Sb: 1.46, Sc: 1.44, Se: 1.22, Si: 1.20,
  Sm: 1.80, Sn: 1.46, Sr: 1.12, Ta: 1.43, Tb: 1.76, Tc: 1.35, Te: 1.47,
  Th: 1.79, Ti: 1.47, Tl: 1.55, Tm: 1.72, U: 1.58, V: 1.33, W: 1.37,
  Y: 1.78, Yb: 1.94, Zn: 1.45, Zr: 1.56,
};

/**
 * Atomic numbers as zero-padded strings
 */
export const ATOMIC_NUMBERS: Record<string, string> = {
  H: '01', He: '02', Li: '03', Be: '04', B: '05', C: '06', N: '07',
  O: '08', F: '09', Ne: '10', Na: '11', Mg: '12', Al: '13', Si: '14',
  P: '15', S: '16', Cl: '17', Ar: '18', K: '19', Ca: '20', Sc: '21',
  Ti: '22', V: '23', Cr: '24', Mn: '25', Fe: '26', Co: '27', Ni: '28',
  Cu: '29', Zn: '30', Ga: '31', Ge: '32', As: '33', Se: '34', Br: '35',
  Kr: '36', Rb: '37', Sr: '38', Y: '39', Zr: '40', Nb: '41', Mo: '42',
  Tc: '43', Ru: '44', Rh: '45', Pd: '46', Ag: '47', Cd: '48', In: '49',
  Sn: '50', Sb: '51', Te: '52', I: '53', Xe: '54', Cs: '55', Ba: '56',
  La: '57', Ce: '58', Pr: '59', Nd: '60', Pm: '61', Sm: '62', Eu: '63',
  Gd: '64', Tb: '65', Dy: '66', Ho: '67', Er: '68', Tm: '69', Yb: '70',
  Lu: '71', Hf: '72', Ta: '73', W: '74', Re: '75', Os: '76', Ir: '77',
  Pt: '78', Au: '79', Hg: '80', Tl: '81', Pb: '82', Bi: '83', Po: '84',
  At: '85', Rn: '86', Fr: '87', Ra: '88', Ac: '89', Th: '90', Pa: '91',
  U: '92', Np: '93', Pu: '94', Am: '95', Cm: '96', Bk: '97', Cf: '98',
  Es: '99', Fm: '100', Md: '101', No: '102', Lr: '103', Rf: '104',
  Db: '105', Sg: '106', Bh: '107', Hs: '108', Mt: '109', Ds: '110',
  Rg: '111', Cn: '112', Nh: '113', Fl: '114', Mc: '115', Lv: '116',
  Ts: '117', Og: '118',
  D: '01', // Deuterium
};

/**
 * Prime numbers assigned to elements for neighbor product calculations.
 * Using primes ensures unique factorization for comparing neighborhood patterns.
 */
export const ELEMENT_PRIMES: Record<string, number> = {
  // Common elements (assigned first primes for efficiency)
  H: 2, C: 3, N: 5, O: 7, F: 11, P: 13, S: 17, Cl: 19, Br: 23, I: 29,
  // Less common elements
  B: 31, Na: 37, Mg: 41, Si: 43, K: 59, Ca: 61, Mn: 67, Fe: 71,
  Co: 73, Ni: 79, Cu: 83, Zn: 89, Al: 97, As: 101, Se: 103,
  Ag: 107, Cd: 109, Sn: 113, Sb: 127, Te: 131, Pt: 137, Au: 139,
  Hg: 149, Pb: 151, Bi: 157,
  D: 2, // Deuterium same as hydrogen
};

/**
 * Get element prime, defaulting to 1 for unknown elements
 */
export function getElementPrime(element: string): number {
  return ELEMENT_PRIMES[element] ?? 1;
}

/**
 * Get atomic radius, defaulting to carbon radius for unknown elements
 */
export function getAtomicRadius(element: string): number {
  return ATOMIC_RADII[element] ?? ATOMIC_RADII['C'];
}

/**
 * Get atomic number string, defaulting to '00' for unknown elements
 */
export function getAtomicNumber(element: string): string {
  return ATOMIC_NUMBERS[element] ?? '00';
}

/**
 * Check if element is hydrogen (or deuterium)
 */
export function isHydrogen(element: string): boolean {
  return element === 'H' || element === 'D';
}

/**
 * Bond distance threshold multiplier.
 * Atoms are considered bonded if distance < (r1 + r2) * BOND_THRESHOLD
 */
export const BOND_THRESHOLD = 1.3;

/**
 * Minimum bond distance (avoid self-bonds)
 */
export const MIN_BOND_DISTANCE = 0.1;
